/**
 * Pannello «Avvisi»: gli avvisi sono del coach. Ognuno si accende o si spegne,
 * ha i suoi limiti (entro l'intervallo che il server dichiara) e una
 * spiegazione di cosa significa e di cosa non dice. Salvare cambia solo gli
 * avvisi di chi salva. Spec: docs/monitoring/DECISIONS.md (D22).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, RotateCcw } from "lucide-react";
import { fetchAPI } from "../../lib/utils";
import {
	ALERT_FORM,
	alertDraftErrors,
	alertDraftPayload,
	splitTemplate,
} from "../../lib/monitoringUtils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from "../ui/dialog";

const cloneAlerts = (alerts) => JSON.parse(JSON.stringify(alerts || {}));

/** Il corpo del pannello, separato dal Dialog per poterlo provare da solo. */
export function AlertSettingsForm({ settings, saving, onSave, onCancel, t }) {
	const [draft, setDraft] = useState(() => cloneAlerts(settings?.alerts));
	useEffect(() => setDraft(cloneAlerts(settings?.alerts)), [settings]);

	const schema = useMemo(() => settings?.schema || {}, [settings]);
	const defaults = useMemo(() => settings?.defaults || {}, [settings]);
	const errors = useMemo(() => alertDraftErrors(draft, schema), [draft, schema]);
	const hasErrors = Object.keys(errors).length > 0;

	const setValue = (code, name, value) =>
		setDraft((current) => ({ ...current, [code]: { ...(current[code] || {}), [name]: value } }));

	return (
		<div className="space-y-3">
			{ALERT_FORM.map((rule) => {
				const values = draft[rule.code] || {};
				const enabled = values.enabled !== false;
				const params = schema[rule.code] || {};
				const defaultsText = Object.keys(params)
					.map((name) => defaults[rule.code]?.[name])
					.join(" / ");
				return (
					<section
						key={rule.code}
						className={`rounded-sm border px-4 py-3 space-y-2 ${enabled ? "border-border bg-card" : "border-border/60 bg-muted/20"}`}
						data-testid={`alert-${rule.code}`}
					>
						<div className="flex items-start justify-between gap-3">
							<div className="font-medium">{t(rule.titleKey)}</div>
							<Switch
								checked={enabled}
								onCheckedChange={(checked) => setValue(rule.code, "enabled", checked)}
								aria-label={t(rule.titleKey)}
							/>
						</div>
						<div className={`text-sm leading-8 ${enabled ? "" : "opacity-50"}`}>
							{splitTemplate(t(rule.sentenceKey)).map((part, index) => {
								if (part.type === "text") return <span key={index}>{part.value}</span>;
								const spec = params[part.name];
								if (!spec) return null;
								const key = `${rule.code}.${part.name}`;
								return (
									<Input
										key={index}
										type="number"
										inputMode="numeric"
										min={spec.min}
										max={spec.max}
										step={1}
										disabled={!enabled}
										value={values[part.name] ?? ""}
										onChange={(event) => setValue(rule.code, part.name, event.target.value)}
										aria-label={`${t(rule.titleKey)} · ${part.name}`}
										aria-invalid={Boolean(errors[key])}
										className={`inline-block w-16 h-8 mx-1 px-2 py-0 text-center rounded-sm align-middle ${errors[key] ? "border-blaze" : ""}`}
										data-testid={`alert-input-${key}`}
									/>
								);
							})}
						</div>
						{Object.keys(params).map((name) => {
							const error = errors[`${rule.code}.${name}`];
							return error ? (
								<p key={name} className="text-xs text-blaze">
									{t("monAlertRange")} {error.min}–{error.max}
								</p>
							) : null;
						})}
						<p className="text-xs text-muted-foreground">{t(rule.explainKey)}</p>
						<p className="text-[11px] text-muted-foreground">
							{t("monAlertDefault")}: {defaultsText}
						</p>
					</section>
				);
			})}
			<div className="flex flex-wrap items-center justify-between gap-2 pt-1">
				<Button
					type="button"
					variant="outline"
					className="rounded-sm"
					onClick={() => setDraft(cloneAlerts(defaults))}
				>
					<RotateCcw className="w-4 h-4 mr-1.5" aria-hidden="true" />
					{t("monAlertReset")}
				</Button>
				<div className="flex gap-2">
					<Button type="button" variant="outline" className="rounded-sm" onClick={onCancel}>
						{t("cancel")}
					</Button>
					<Button
						type="button"
						className="rounded-sm"
						disabled={hasErrors || saving}
						onClick={() => onSave(alertDraftPayload(draft, schema))}
					>
						{saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden="true" /> : null}
						{t("save")}
					</Button>
				</div>
			</div>
		</div>
	);
}

export function AlertSettingsDialog({ open, onOpenChange, onSaved, t }) {
	const [settings, setSettings] = useState(null);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (!open) return;
		let cancelled = false;
		fetchAPI("/coach/monitoring/settings")
			.then((payload) => !cancelled && setSettings(payload))
			.catch((error) => toast.error(error?.message || t("monLoadError")));
		return () => {
			cancelled = true;
		};
	}, [open, t]);

	const save = async (alerts) => {
		setSaving(true);
		try {
			const payload = await fetchAPI("/coach/monitoring/settings", {
				method: "PUT",
				body: JSON.stringify({ alerts }),
			});
			setSettings(payload);
			toast.success(t("monAlertSaved"));
			onSaved?.(payload);
			onOpenChange(false);
		} catch (error) {
			toast.error(error?.message || t("monAlertSaveError"));
		} finally {
			setSaving(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-sm p-4 sm:p-6">
				<DialogHeader>
					<DialogTitle>{t("monAlertSettingsTitle")}</DialogTitle>
					<DialogDescription>{t("monAlertSettingsIntro")}</DialogDescription>
				</DialogHeader>
				{settings ? (
					<AlertSettingsForm
						settings={settings}
						saving={saving}
						onSave={save}
						onCancel={() => onOpenChange(false)}
						t={t}
					/>
				) : (
					<div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
						<Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
						{t("loading")}
					</div>
				)}
			</DialogContent>
		</Dialog>
	);
}
